<template>
  <KekkleBottomSheet v-model:open="isOpen" shadow="blue" padding="normal">
    <div class="dark:text-slate-200">
      <h2 class="mb-4 text-xl font-bold dark:text-slate-200">
        👥 {{ $t("pages.invite.title") }}
      </h2>

      <div v-if="hasActiveToken">
        <information-text
          class="mb-4"
          :information="$t('pages.invite.link_explained')"
        >
          💡
        </information-text>

        <p
          class="relative mb-4 cursor-pointer select-text break-words rounded-lg border-2 border-kekkle-dark-dark bg-gray-200 p-2 text-center text-2xl text-gray-500 dark:bg-kekkle-dark-light dark:text-slate-200"
          @click="copyToken()"
        >
          <clipboard-icon class="absolute right-2 top-3 h-6"></clipboard-icon>
          🔑 {{ joinToken }}
        </p>

        <p
          class="relative mb-4 cursor-pointer select-text break-words rounded-lg border-2 border-kekkle-dark-dark bg-gray-200 p-2 pr-6 text-gray-500 dark:bg-kekkle-dark-light dark:text-slate-200"
          @click="share()"
        >
          <span
            v-if="canShare || hasSupportForFlutterShare()"
            class="absolute right-2 top-2"
          >
            🚀
          </span>
          <clipboard-icon
            v-else
            class="absolute right-2 top-2 h-6"
          ></clipboard-icon>
          🔗 {{ inviteUrl }}
        </p>

        <p class="dark:text-slate-200">
          ⏰ {{ $t("pages.invite.link_remaining_time", [timeLeft]) }}
        </p>

        <p
          v-if="!linkLocallyDeleted"
          class="w-min cursor-pointer truncate py-1 text-red-500"
          @click="deleteLink()"
        >
          🗑️ {{ $t("pages.invite.delete_link") }}
        </p>
      </div>

      <div v-if="!hasActiveToken" class="my-4">
        <p class="mb-4 text-sm dark:text-slate-200">
          {{ $t("pages.invite.no_link_explanation") }}
        </p>

        <default-button
          :disabled="status.loading"
          :loading="status.loading"
          @click="generateLink()"
        >
          <p class="font-medium">{{ $t("pages.invite.generate_link") }}</p>
        </default-button>
      </div>

      <div v-else class="my-4">
        <default-button
          :disabled="status.loading"
          :loading="status.loading"
          class="mt-4"
          @click="generateLink()"
        >
          <p class="font-medium">{{ $t("pages.invite.generate_new_link") }}</p>
        </default-button>
      </div>
    </div>
  </KekkleBottomSheet>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { logEvent } from "firebase/analytics";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";
import { setErrorToast } from "@/helpers/notifications/toast";
import { firebaseAnalytics } from "@/helpers/firebase/firebase";
import { formatDuration } from "@/helpers/dates/format-duration";
import {
  deleteInviteLink,
  generateInviteLink,
} from "@/helpers/firestore/InviteLinkService";
import { useClipboardAndShare } from "@/composables/useClipboardAndShare";
import { hasSupportForFlutterShare } from "@/helpers/flutter/capabilities";
import InformationText from "./InformationText.vue";
import { ClipboardIcon } from "@heroicons/vue/outline";
import KekkleBottomSheet from "./ui/KekkleBottomSheet.vue";
import DefaultButton from "./Buttons/DefaultButton.vue";

const { t } = useI18n();
const groupsStore = useGroupsStore();
const userStore = useUserStore();
const { canShare, copyToClipboard, shareContent } = useClipboardAndShare();

const isOpen = defineModel<boolean>("open", { required: true });

const linkLocallyDeleted = ref(false);
const currentTime = ref(new Date());
const status = ref({
  loading: false,
  hasGeneratedLink: false,
});

const joinToken = computed(() => {
  if (groupsStore.groupData?.["join_token"]) {
    return groupsStore.groupData["join_token"];
  }
  return "";
});

const validTill = computed(() => {
  if (groupsStore.groupData?.["join_token_timestamp"]) {
    return groupsStore.groupData?.["join_token_timestamp"];
  }
  return new Date();
});

const timeLeft = computed(() => {
  if (hasActiveToken.value) {
    const timeLeftMs = Math.abs(
      (validTill.value as Date).getTime() - currentTime.value.getTime(),
    );
    return formatDuration(timeLeftMs, t);
  }
  return "";
});

const hasActiveToken = computed(() => {
  console.log(validTill.value);
  if (validTill.value && joinToken.value) {
    return (validTill.value as Date) > currentTime.value;
  }
  return false;
});

const inviteUrl = computed(() => {
  return window.location.origin + "/invite-link?token=" + joinToken.value;
});

// Update current time every second
let timeInterval: ReturnType<typeof setInterval> | null = null;

watch(
  isOpen,
  (newValue) => {
    if (newValue) {
      // Start interval when sheet opens
      timeInterval = setInterval(() => {
        currentTime.value = new Date();
      }, 1000);
    } else {
      // Clear interval when sheet closes
      if (timeInterval) {
        clearInterval(timeInterval);
        timeInterval = null;
      }
    }
  },
  { immediate: true },
);

function copyToken() {
  copyToClipboard(
    joinToken.value,
    "global.toast.code_copied",
    "invite_members",
  );
}

function share() {
  const shareOptions = {
    title: t("pages.invite.join_kekkle_title"),
    subject:
      t("pages.invite.you_are_invited_for") +
      (groupsStore.groupData?.name ?? ""),
  };

  shareContent(inviteUrl.value, shareOptions, "invite_members");
}

async function deleteLink() {
  if (!userStore.activeGroup) return;

  logEvent(firebaseAnalytics, "invite_members", {
    name: "deleted_link",
  });

  linkLocallyDeleted.value = true;

  try {
    await deleteInviteLink(userStore.activeGroup);
    await groupsStore.refreshAllUserGroupsData();
    linkLocallyDeleted.value = false;
  } catch (error) {
    console.error("Failed to delete invite link:", error);
    linkLocallyDeleted.value = false;
  }
}

async function generateLink() {
  if (!userStore.activeGroup) return;

  logEvent(firebaseAnalytics, "invite_members", {
    name: "generated_link",
  });

  linkLocallyDeleted.value = false;
  status.value.loading = true;

  try {
    await generateInviteLink(userStore.activeGroup, 168); // 7 days = 168 hours
    status.value.hasGeneratedLink = true;
    await groupsStore.refreshCurrentGroupData();
  } catch (error) {
    console.error("Failed to generate invite link:", error);
    setErrorToast({
      message: t("global.toast.generate_link_failed"),
    });
  } finally {
    status.value.loading = false;
  }
}
</script>
